import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Quick add 仅使用静态单图预览，不替换整组商品图库。
    if (this.closest('quick-add-dialog')) return;

    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        // 同商品变体切换只回传了一张图;组合商品换的是另一个商品,整组图不同,只能整块换
        if (newMediaGallery.dataset.variantMediaOnly === 'true') {
          this.#swapVariantMedia(newMediaGallery);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * 只把当前变体主图那一张就地换掉,其余不动 —— 图库第 2 张往后与变体无关(hide_variants)。
   * - partial:只含一张图的 media-gallery(带 data-variant-media-only)
   */
  #swapVariantMedia(partial) {
    // 移动端轮播 / PC 网格 / 放大弹窗缩略图 / 放大弹窗大图,四处渲染同一组媒体
    const lists = ['slideshow-slides', '.media-gallery__grid', '.dialog-thumbnails-list', '.dialog-zoomed-gallery'];

    let changed = false;

    for (const selector of lists) {
      const current = this.querySelector(selector)?.firstElementChild;
      const next = partial.querySelector(selector)?.firstElementChild;

      if (!current || !next) continue;
      if (current.isEqualNode(next)) continue; // 同色换尺码:主图没变,不动 DOM

      current.replaceWith(next);
      changed = true;
    }

    if (changed) this.slideshow?.select(0, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
